import { css, html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import {
    TeqtankCommerce,
    type Commerce,
    type CommerceDataSource,
    type CommerceOrder,
} from "@teqtank/commerce-sdk";
import "./teqtank-order-status.js";
import "./teqtank-order-items.js";
import "./teqtank-order-totals.js";
@customElement("teqtank-order-complete")
export class TeqtankOrderComplete extends LitElement {
    @property({ attribute: false }) public accessor commerce: Commerce | undefined;
    @property({ attribute: "order-id" }) public accessor orderId = "";
    @property({ attribute: "commerce-key" }) public accessor commerceKey = "demo";
    @property({ attribute: "data-source" }) public accessor dataSource: CommerceDataSource =
        "playground";
    @property({ attribute: "shop-route" }) public accessor shopRoute = "/shop";
    @property({ attribute: "orders-route" }) public accessor ordersRoute = "/account/orders";
    @property({ attribute: "continuation-route" }) public accessor continuationRoute = "";
    @property({ attribute: "continuation-label" }) public accessor continuationLabel = "";
    @property({ attribute: "continuation-stage-id" }) public accessor continuationStageId = "";
    @property({ attribute: "continuation-stage-kind" }) public accessor continuationStageKind = "";
    @state() private accessor order: CommerceOrder | undefined;
    @state() private accessor loading = true;
    @state() private accessor errorMessage = "";
    private ownsCommerce = false;
    public static override styles = css`
        :host {
            container-type: inline-size;
            --tt-ink: #18221e;
            --teqtank-color-text-muted: #69736d;
            --teqtank-color-accent: #8d5c3f;
            --teqtank-color-surface-soft: #f7f4ee;
            --tt-line: #ded8cf;
            display: block;
            color: var(--tt-ink);
            font-family: Inter, ui-sans-serif, system-ui, sans-serif;
        }
        .shell {
            max-width: 1040px;
            margin: 0 auto;
            padding: clamp(2rem, 6cqw, 5rem) clamp(1rem, 3cqw, 2rem);
        }
        .hero {
            display: grid;
            gap: 1rem;
            text-align: center;
            margin-bottom: 2rem;
        }
        .hero h1 {
            font:
                500 clamp(2.5rem, 6cqw, 4.6rem)/1 Georgia,
                serif;
            margin: 0;
            letter-spacing: -0.04em;
        }
        .eyebrow {
            font-size: 0.72rem;
            letter-spacing: 0.14em;
            font-weight: 800;
            color: var(--teqtank-color-accent);
        }
        .card {
            border: 1px solid var(--tt-line);
            border-radius: 1.25rem;
            background: #fff;
            padding: clamp(1rem, 3cqw, 1.6rem);
        }
        .grid {
            display: grid;
            grid-template-columns: minmax(0, 1.4fr) minmax(280px, 0.8fr);
            gap: 1.25rem;
            margin-top: 1.25rem;
        }
        .meta {
            display: grid;
            gap: 0.75rem;
            color: var(--teqtank-color-text-muted);
            font-size: 0.9rem;
        }
        .meta strong {
            color: var(--tt-ink);
        }
        .actions {
            display: flex;
            gap: 0.75rem;
            flex-wrap: wrap;
            justify-content: center;
            margin-top: 2rem;
        }
        .actions a {
            padding: 0.95rem 1.3rem;
            border-radius: 999px;
            text-decoration: none;
            font-weight: 750;
        }
        .primary {
            background: var(--tt-ink);
            color: #fff;
        }
        .secondary {
            border: 1px solid var(--tt-line);
            color: var(--tt-ink);
        }
        .error {
            padding: 1rem;
            border-radius: 0.8rem;
            background: #fff2ef;
            color: #9b2e1f;
        }
        @container (max-width: 760px) {
            .grid {
                grid-template-columns: 1fr;
            }
        }
    `;
    protected override async firstUpdated(): Promise<void> {
        await this.load();
    }
    public override disconnectedCallback(): void {
        this.commerce?.orders.cancelPolling();
        if (this.ownsCommerce) this.commerce?.dispose();
        super.disconnectedCallback();
    }
    protected override render() {
        if (this.loading)
            return html`<div class="shell">
                <div class="hero">
                    <p class="eyebrow">ORDER PROCESSING</p>
                    <h1>Completing your order…</h1>
                    <p>Payment is approved. We are waiting for final confirmation.</p>
                </div>
            </div>`;
        if (this.errorMessage)
            return html`<div class="shell">
                <div class="error" role="alert">${this.errorMessage}</div>
            </div>`;
        const o = this.order;
        if (!o) return nothing;
        return html`<main class="shell">
            <header class="hero">
                <p class="eyebrow">${o.status === "completed" ? "THANK YOU" : "ORDER UPDATE"}</p>
                <h1>
                    ${
                        o.status === "completed"
                            ? "Your order is confirmed."
                            : "We’re finalising your order."
                    }
                </h1>
                <p>Order <strong>${o.orderNumber}</strong> · ${o.email}</p>
            </header>
            <section class="card">
                <teqtank-order-status .status=${o.status}></teqtank-order-status>
            </section>
            <div class="grid">
                <section class="card">
                    <h2>Order details</h2>
                    <teqtank-order-items .items=${o.items}></teqtank-order-items>
                </section>
                <aside class="card">
                    <h2>Summary</h2>
                    <teqtank-order-totals .totals=${o.totals}></teqtank-order-totals>
                    <div class="meta">
                        <p>
                            <strong>Delivery</strong><br />${
                                o.shipments[0]?.methodName ?? "To be confirmed"
                            }<br />${o.shipments[0]?.estimatedDelivery ?? ""}
                        </p>
                        <p><strong>Payment</strong><br />${o.paymentSummary ?? "Confirmed"}</p>
                        ${
                            o.receipt
                                ? html`<p><strong>Receipt</strong><br />${o.receipt.number}</p>`
                                : nothing
                        }
                    </div>
                </aside>
            </div>
            <nav class="actions">
                ${
                    this.continuationLabel
                        ? html`<a
                              class="primary"
                              href=${this.continuationRoute || "#"}
                              @click=${this.continueJourney}
                              >${this.continuationLabel}</a
                          >`
                        : html`<a class="primary" href=${this.shopRoute}>Continue shopping</a>`
                }
                <a class="secondary" href=${this.ordersRoute}>View my orders</a>
            </nav>
        </main>`;
    }
    private readonly continueJourney = (event: MouseEvent): void => {
        const navigate = this.dispatchEvent(
            new CustomEvent("teqtank-order-continuation-requested", {
                detail: {
                    route: this.continuationRoute,
                    stageId: this.continuationStageId,
                    stageKind: this.continuationStageKind,
                },
                bubbles: true,
                composed: true,
                cancelable: true,
            }),
        );
        if (!navigate || !this.continuationRoute) event.preventDefault();
    };
    private async load(): Promise<void> {
        try {
            if (!this.orderId.trim()) throw new Error("Order ID is required.");
            if (this.commerce === undefined) {
                this.commerce = await TeqtankCommerce.initialize({
                    commerceKey: this.commerceKey,
                    dataSource: this.dataSource,
                });
                this.ownsCommerce = true;
            }
            this.order = await this.commerce.orders.get(this.orderId);
            if (this.order.status === "pending" || this.order.status === "processing")
                this.order = await this.commerce.orders.waitForCompletion(this.orderId);
            this.dispatchEvent(
                new CustomEvent(
                    this.order.status === "completed"
                        ? "teqtank-order-completed"
                        : "teqtank-order-failed",
                    {
                        detail: { order: this.order },
                        bubbles: true,
                        composed: true,
                    },
                ),
            );
        } catch (error) {
            this.errorMessage =
                error instanceof Error ? error.message : "Order confirmation could not be loaded.";
        } finally {
            this.loading = false;
        }
    }
}
declare global {
    interface HTMLElementTagNameMap {
        "teqtank-order-complete": TeqtankOrderComplete;
    }
}
